<template>
  <div class="chat-panel">
    <div class="chat-panel-messages" ref="messagesContainer">
      <template v-if="initializing">
        <div class="chat-skeleton-wrap">
          <div class="chat-skeleton-avatar" />
          <div class="chat-skeleton-lines">
            <div class="chat-skeleton-line" style="width:72%" />
            <div class="chat-skeleton-line" style="width:52%" />
          </div>
        </div>
      </template>
      <template v-else v-for="(message, index) in messages">
        <system-message v-if="message.role === 'system'" :key="'s-' + index" :text="message.text" :content="message.content" :timestamp="message.timestamp" :is-last="index === messages.length - 1" :streaming="!!message.streaming" @action="handleAction($event, index)" />
        <user-message v-else-if="message.role === 'user'" :key="'u-' + index" :text="message.text" :initials="userInitials" :timestamp="message.timestamp" />
      </template>
    </div>
    <ChatInputBar ref="inputBar" :loading="loading || initializing" :locked="!!context.chat_locked" :card-options="lastCardOptions" @send="handleSend" />
  </div>
</template>

<script>
import ChatInputBar from '../../../common/chat-panel/ChatInputBar.vue'
import SystemMessage from '../../../common/chat-panel/SystemMessage.vue'
import UserMessage from '../../../common/chat-panel/UserMessage.vue'
import api from '../../../../../utils/api'
import { getAccessToken } from '../../../../../utils/auth'
import { llmResponseToContent } from './llmResponseToContent'
import trackEvent from '../../../../../utils/trackEvent'

export default {
  name: 'ChatPanel',
  components: { ChatInputBar, SystemMessage, UserMessage },
  data () {
    const username = localStorage.getItem('username') || ''
    const parts = username.split('.')
    const firstName = parts[0].replace(/\b\w/g, c => c.toUpperCase())
    const initials = parts
      .filter(p => p.length > 0)
      .map(p => p[0].toUpperCase())
      .slice(0, 2)
      .join('')
    return {
      initializing: true,
      loading: false,
      userInitials: initials || 'U',
      userId: username,
      firstName,
      chatId: null,
      messageSequence: 0,
      messages: [],
      lastCardOptions: null,
      context: {
        step: 'intake',
        currentField: null,
        country: null,
        audience: null,
        cta: null,
        programme: null,
        customer: null,
        channels: null,
        journey_id: null,
        stage_id: null,
        ad_id: null,
        phrase: null,
        welcome_messages: null,
        keyword: null,
        programme_defaults: null,
        campaign_metadata: null
      }
    }
  },
  async mounted () {
    try {
      await this.initChat()
    } finally {
      this.initializing = false
      this.$emit('ready')
    }
  },
  watch: {
    messages () {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    'context.chat_locked' (val, old) {
      if (val && !old) {
        trackEvent({ event_name: 'chat_locked', ai_console_chat_id: this.chatId, properties: { entry_point_id: null } })
      }
    },
    'context.journey_id' (val, old) {
      if (val && !old) {
        trackEvent({ event_name: 'journey_selected_from_list', ai_console_chat_id: this.chatId, properties: { journey_id: val, stage_id: this.context.stage_id || null } })
      }
    },
    'context.step' (val, old) {
      if (old === 'metadata' && val !== 'metadata') {
        const meta = this.context.campaign_metadata || {}
        const fieldsProvided = Object.entries(meta).filter(([, v]) => v).map(([k]) => k)
        const hasData = fieldsProvided.length > 0
        trackEvent({
          event_name: hasData ? 'campaign_metadata_added' : 'campaign_metadata_skipped',
          ai_console_chat_id: this.chatId,
          properties: hasData ? { fields_provided: fieldsProvided } : {}
        })
      }
    }
  },
  methods: {
    async initChat () {
      try {
        const { data: listData } = await api.buildRequest('ai-console-chat', 'get', { user_id: this.userId, last: 'true' })
        const existing = listData && listData.payload && (Array.isArray(listData.payload) ? listData.payload[0] : listData.payload)

        if (existing && !existing.is_completed) {
          this.chatId = existing.id
          await this.restoreHistory()
        } else {
          await this.createChat()
        }
      } catch (err) {
        await this.createChat()
      }
    },

    async createChat () {
      const greeting = {
        role: 'system',
        text: `Hi ${this.firstName}! To get started, please describe your ad context in text. This helps me infer the right programme, journey, and messaging.`,
        timestamp: Date.now()
      }
      try {
        const { data } = await api.buildRequest('ai-console-chat', 'post', {
          user_id: this.userId,
          purpose: 'Keyword creation'
        })
        const session = data && data.payload
        if (session && session.id) {
          this.chatId = session.id
          this.messages = [greeting]
          await this.saveTurn('bot', this.userId, greeting.text)
          return
        }
      } catch (err) {
        console.error('Failed to create chat session', err)
      }
      this.messages = [greeting]
    },

    async restoreHistory () {
      try {
        const { data } = await api.buildRequest(`ai-console-chat/${this.chatId}/history`, 'get')
        const history = data && data.payload
        if (!Array.isArray(history) || !history.length) return

        this.messageSequence = history[history.length - 1].message_sequence
        this.messages = history.map(turn => {
          const isBot = turn.sender === 'bot'
          let content = null
          let text = turn.message

          if (isBot && turn.message) {
            try {
              const parsed = JSON.parse(turn.message)
              content = llmResponseToContent(parsed)
              text = null
            } catch (_) {}
          }

          return {
            role: isBot ? 'system' : 'user',
            text,
            content,
            timestamp: turn.created_at ? new Date(turn.created_at).getTime() : Date.now()
          }
        })
      } catch (err) {
        console.error('Failed to restore chat history', err)
      }
    },

    async saveTurn (sender, receiver, message) {
      if (!this.chatId) return
      this.messageSequence += 1
      try {
        await api.buildRequest(`ai-console-chat/${this.chatId}`, 'put', {
          turn: { sender, receiver, message, message_sequence: this.messageSequence }
        })
      } catch (err) {
        console.error('Failed to save turn', err)
      }
    },

    async markCompleted () {
      if (!this.chatId) return
      try {
        const phrases = [].concat(this.context.phrase || []).filter(Boolean)
        const langs = this.context.active_languages || this.context.welcome_messages
          ? Object.keys(this.context.welcome_messages || {}) : []
        trackEvent({
          event_name: 'entry_point_confirmed',
          ai_console_chat_id: this.chatId,
          properties: {
            channels: this.context.channels,
            market: this.context.country,
            journey_id: this.context.journey_id,
            stage_id: this.context.stage_id,
            phrase_count: phrases.length,
            language_count: langs.length || 1
          }
        })
        await api.buildRequest(`ai-console-chat/${this.chatId}`, 'put', { is_completed: true })
      } catch (err) {
        console.error('Failed to complete chat session', err)
      }
    },

    handleAction (action, messageIndex) {
      const { type, value, elementIndex } = action
      let text = null
      if (type === 'button') text = value
      else if (type === 'chip') text = value
      else if (type === 'chip-multi-confirm') text = Array.isArray(value) ? value.join(', ') : value
      else if (type === 'card-confirm') {
        // Resolve card index to title so LLM gets the actual value, not a number
        const msg = this.messages[messageIndex]
        const cardEl = msg && msg.content && msg.content[elementIndex]
        const card = cardEl && cardEl.items && cardEl.items[value]
        text = card ? card.title : (value !== undefined ? String(value) : null)
      } else if (type === 'form') {
        const filled = Object.entries(value).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`)
        text = filled.length ? filled.join(', ') : 'Submitted'
        this._pendingFormJson = JSON.stringify(value)
      } else if (type === 'upload-skip') {
        text = 'skip'
      } else if (type === 'upload') {
        this.$emit('file-upload', { file: value, messageIndex })
        return
      }
      if (['button', 'chip', 'chip-multi-confirm', 'card-confirm'].includes(type)) {
        trackEvent({ event_name: 'ai_suggestion_accepted', ai_console_chat_id: this.chatId, properties: { suggestion_type: type, step: this.context.step } })
      }
      if (text) {
        this.$refs.inputBar.clear()
        this.handleSend(text)
      }
    },
    async sendGenerateTrigger () {
      this.loading = true
      this._typewriterQueue = []
      this._typewriterTimer = null
      this._doneEvent = null

      const streamIdx = this.messages.length
      this.messages.push({ role: 'system', text: '', content: null, timestamp: Date.now(), streaming: true })

      try {
        const host = window.location.hostname + (window.location.hostname === 'localhost' ? ':8080' : '')
        const response = await fetch(`${window.location.protocol}//${host}/api/llm/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
          body: JSON.stringify({ message: 'first_visit', history: this.messages.slice(0, streamIdx - 1), context: this.context, chat_id: this.chatId, user_id: this.userId })
        })

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let sseBuffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          sseBuffer += decoder.decode(value, { stream: true })
          const lines = sseBuffer.split('\n')
          sseBuffer = lines.pop()
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const event = JSON.parse(line.slice(6))
            if (event.type === 'token') {
              this._typewriterQueue.push(event.value)
              this._flushToken(streamIdx)
            } else if (event.type === 'done') {
              if (this._typewriterTimer || this._typewriterQueue.length > 0) {
                this._doneEvent = event
              } else {
                if (event.context) this.context = { ...this.context, ...event.context }
                if (event.entryPointData && !event.entryPointData.error) {
                  await this.markCompleted()
                  trackEvent({ event_name: 'entry_point_created', ai_console_chat_id: this.chatId, properties: { entry_point_id: event.entryPointData && event.entryPointData.entry_point_id, source: 'console' } })
                  this.$emit('entry-point-created', event.entryPointData)
                }
                this.updateCardOptions(event.reply)
                const content = llmResponseToContent(event.reply)
                this.messages.splice(streamIdx, 1, { role: 'system', text: content ? null : event.reply.message, content, timestamp: Date.now(), streaming: false })
                this.saveTurn('bot', this.userId, JSON.stringify(event.reply))
              }
            } else if (event.type === 'error') {
              if (this._typewriterTimer) { clearInterval(this._typewriterTimer); this._typewriterTimer = null }
              this._typewriterQueue = []
              this.messages.splice(streamIdx, 1, { role: 'system', text: 'Something went wrong while creating the entry point. Please try again.', timestamp: Date.now(), streaming: false })
            }
          }
        }
      } catch (err) {
        if (this._typewriterTimer) { clearInterval(this._typewriterTimer); this._typewriterTimer = null }
        this._typewriterQueue = []
        this.messages.splice(streamIdx, 1, { role: 'system', text: 'Something went wrong while creating the entry point. Please try again.', timestamp: Date.now(), streaming: false })
      } finally {
        this.loading = false
      }
    },

    _flushToken (streamIdx) {
      if (this._typewriterTimer) return
      this._typewriterTimer = setInterval(async () => {
        if (this._typewriterQueue.length > 0) {
          const char = this._typewriterQueue.shift()
          const msg = this.messages[streamIdx]
          this.messages.splice(streamIdx, 1, { ...msg, text: (msg.text || '') + char })
        } else if (this._doneEvent) {
          clearInterval(this._typewriterTimer)
          this._typewriterTimer = null
          const event = this._doneEvent
          this._doneEvent = null
          const prevStep = this.context.step
          if (event.context) this.context = { ...this.context, ...event.context }
          if (this.context.step === 'generate' && prevStep !== 'generate') await this.sendGenerateTrigger()
          if (event.entryPointData && !event.entryPointData.error) {
            this.markCompleted()
            trackEvent({ event_name: 'entry_point_created', ai_console_chat_id: this.chatId, properties: { entry_point_id: event.entryPointData && event.entryPointData.entry_point_id, source: 'console' } })
            this.$emit('entry-point-created', event.entryPointData)
          }
          this.updateCardOptions(event.reply)
          const content = llmResponseToContent(event.reply)
          this.messages.splice(streamIdx, 1, { role: 'system', text: content ? null : event.reply.message, content, timestamp: Date.now(), streaming: false })
          this.saveTurn('bot', this.userId, JSON.stringify(event.reply))
        }
      }, 22)
    },

    updateCardOptions (reply) {
      const ui = reply && reply.ui
      // Only show the "doesn't match" hint for fields backed by real validation (country,
      // customer, channels, journey card_select) — audience/cta chips are explicitly
      // suggestions the prompt is told to accept free text for, and the metadata yes/skip
      // question is an intent classification the AI is meant to interpret, not a strict
      // pick-from-this-list field. Flagging free text as a "mismatch" there is just wrong.
      const softFields = ['audience', 'cta']
      const isSoftContext = this.context.step === 'metadata' || softFields.includes(this.context.currentField)
      this.lastCardOptions = ui && ['chips', 'card_select'].includes(ui.type) && !isSoftContext
        ? (ui.options || []).map(o => (typeof o === 'string' ? o : o.title)).filter(Boolean)
        : null
    },

    async handleSend (text) {
      if (!text || this.loading) return
      const llmMessage = this._pendingFormJson || text
      this._pendingFormJson = null
      this.messages.push({ role: 'user', text, timestamp: Date.now() })
      this.saveTurn(this.userId, 'bot', text)
      trackEvent({ event_name: 'chat_turn_sent', ai_console_chat_id: this.chatId, properties: { step: this.context.step, turn_number: this.messageSequence + 1, message_length: text.length } })
      this.loading = true

      this._typewriterQueue = []
      this._typewriterTimer = null
      this._doneEvent = null

      const streamIdx = this.messages.length
      this.messages.push({ role: 'system', text: '', content: null, timestamp: Date.now(), streaming: true })

      try {
        const host = window.location.hostname + (window.location.hostname === 'localhost' ? ':8080' : '')
        const response = await fetch(`${window.location.protocol}//${host}/api/llm/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
          body: JSON.stringify({ message: llmMessage, history: this.messages.slice(0, streamIdx - 1), context: this.context, chat_id: this.chatId, user_id: this.userId })
        })

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let sseBuffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          sseBuffer += decoder.decode(value, { stream: true })
          const lines = sseBuffer.split('\n')
          sseBuffer = lines.pop()
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const event = JSON.parse(line.slice(6))
            if (event.type === 'token') {
              this._typewriterQueue.push(event.value)
              this._flushToken(streamIdx)
            } else if (event.type === 'done') {
              if (this._typewriterTimer || this._typewriterQueue.length > 0) {
                this._doneEvent = event
              } else {
                const prevStep = this.context.step
                if (event.context) this.context = { ...this.context, ...event.context }
                if (this.context.step === 'generate' && prevStep !== 'generate') await this.sendGenerateTrigger()
                if (event.entryPointData && !event.entryPointData.error) {
                  await this.markCompleted()
                  trackEvent({ event_name: 'entry_point_created', ai_console_chat_id: this.chatId, properties: { entry_point_id: event.entryPointData && event.entryPointData.entry_point_id, source: 'console' } })
                  this.$emit('entry-point-created', event.entryPointData)
                }
                this.updateCardOptions(event.reply)
                const content = llmResponseToContent(event.reply)
                this.messages.splice(streamIdx, 1, { role: 'system', text: content ? null : event.reply.message, content, timestamp: Date.now(), streaming: false })
                this.saveTurn('bot', this.userId, JSON.stringify(event.reply))
              }
            } else if (event.type === 'error') {
              if (this._typewriterTimer) { clearInterval(this._typewriterTimer); this._typewriterTimer = null }
              this._typewriterQueue = []
              this.messages.splice(streamIdx, 1, { role: 'system', text: 'Something went wrong. Please try again.', timestamp: Date.now(), streaming: false })
            }
          }
        }
      } catch (err) {
        if (this._typewriterTimer) { clearInterval(this._typewriterTimer); this._typewriterTimer = null }
        this._typewriterQueue = []
        this.messages.splice(streamIdx, 1, { role: 'system', text: 'Something went wrong. Please try again.', timestamp: Date.now(), streaming: false })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
<style scoped src="./ChatPanel.css"></style>



// WEBPACK FOOTER //
// ChatPanel.vue?c8857d7e